import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { apiUrl, basePath } from '../../api/envUrl';
import { invoiceDate } from '../../utils/dates';

const money = (value?: number | null) => Number(value || 0).toFixed(2);

interface History {
  customer: Record<string, unknown>;
  billCount: number;
  totalSpent: number;
  outstanding: number;
  purchases: Record<string, any>[];
  handsets: Record<string, any>[];
}

/**
 * One customer's whole story: what they bought, what they still owe, and which
 * handsets are theirs.
 *
 * <p>The handsets are listed separately because that is the question actually asked
 * at the counter - somebody walks in with a phone and wants to know whether the shop
 * sold it and when, which is what decides the warranty.
 */
const CustomerHistory = ({ customerId, onClose }: { customerId: number; onClose: () => void }) => {
  const [data, setData] = useState<History | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const token = localStorage.getItem('mobileShopToken');
        const response = await fetch(`${apiUrl}${basePath}/customer/${customerId}/history`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        const body = await response.json();
        // The dialog can be closed while this is still in flight.
        if (live) setData(body);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [customerId]);

  const customer = (data?.customer ?? {}) as Record<string, any>;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography fontWeight={700} fontSize="1rem">
            {customer.customerName ?? 'Customer'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {[customer.mobileNumber, customer.address].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && data && (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={4}>
                <Card sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    BILLS
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {data.billCount}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={4}>
                <Card sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    TOTAL SPENT
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {money(data.totalSpent)}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={4}>
                <Card sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    STILL OWES
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color={Number(data.outstanding) > 0 ? 'error.main' : 'success.main'}>
                    {money(data.outstanding)}
                  </Typography>
                </Card>
              </Grid>
            </Grid>

            <Typography variant="subtitle2" fontWeight={700} color="primary.main" sx={{ mb: 1 }}>
              PURCHASES
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Bill</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Paid by</TableCell>
                  <TableCell align="center">Items</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.purchases.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      Nothing bought yet.
                    </TableCell>
                  </TableRow>
                )}
                {data.purchases.map((bill) => (
                  <TableRow key={bill.billId} hover>
                    <TableCell>
                      {bill.billNo}
                      {bill.status === 'CANCELLED' && <Chip size="small" color="error" variant="outlined" label="Cancelled" sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell>{invoiceDate(bill.billDate)}</TableCell>
                    <TableCell>{bill.paymentType}</TableCell>
                    <TableCell align="center">{bill.itemCount}</TableCell>
                    <TableCell align="right">{money(bill.totalAmount)}</TableCell>
                    {/* An EMI bill's balance is zero because the finance company settles
                        it - the customer owes the shop nothing. Showing only "0.00" on a
                        23,600 sale reads as if it were paid in full, so the financier's
                        share is named instead. */}
                    <TableCell align="right" sx={{ color: Number(bill.balanceAmount) > 0 ? 'error.main' : 'text.secondary' }}>
                      {bill.paymentType === 'EMI' && Number(bill.financedAmount) > 0 ? (
                        <Stack spacing={0} alignItems="flex-end">
                          <Typography variant="body2" color="text.secondary">
                            {money(bill.financedAmount)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            with the financier
                          </Typography>
                          {Number(bill.downPayment) > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {money(bill.downPayment)} paid here
                            </Typography>
                          )}
                        </Stack>
                      ) : (
                        money(bill.balanceAmount)
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {data.handsets.length > 0 && (
              <>
                <Typography variant="subtitle2" fontWeight={700} color="primary.main" sx={{ mt: 3, mb: 1 }}>
                  HANDSETS BOUGHT HERE
                </Typography>
                <Stack gap={1}>
                  {data.handsets.map((phone) => (
                    <Card key={phone.imeiNo} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {phone.productName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Serial No {phone.imeiNo} &middot; {phone.billNo} &middot; {phone.billDate}
                          </Typography>
                        </Box>
                        <Typography variant="body2">{money(phone.rate)}</Typography>
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CustomerHistory;
